import { Box, Button, IconButton, TextField } from "@mui/material";
import { alpha } from "@mui/material/styles";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useState } from "react";
import type { JSX } from "react";

import { BulkCheckbox } from "../../../../components/bulk-selection/index.js";
import { StatusBadge } from "../../components/StatusBadge.js";
import type { SupportStatus } from "../../types.js";
import { STATUS_GRID } from "./grids.js";

/** One editable ticket-status row: select checkbox, live badge, name field,
 * colour picker, the (read-only) reporting category, Save (enabled only when
 * dirty), and Delete. Extracted from `SettingsWorkflowsPanel.tsx`. */
export function StatusRow({
  status,
  selected,
  onToggle,
  onSave,
  onDelete,
  isPending,
}: {
  status: SupportStatus;
  selected: boolean;
  onToggle: (checked: boolean, opts: { shiftKey: boolean }) => void;
  onSave: (body: Partial<SupportStatus>) => void;
  onDelete: () => void;
  isPending: boolean;
}): JSX.Element {
  const [name, setName] = useState(status.name);
  const [color, setColor] = useState(status.color ?? "#64748b");
  const dirty = name !== status.name || color !== (status.color ?? "#64748b");
  return (
    <Box
      component="li"
      sx={[
        { display: "grid", gridTemplateColumns: STATUS_GRID, alignItems: "center", gap: 1.5, borderRadius: 1, border: 1, p: 1, transition: "background-color 150ms" },
        selected
          ? { borderColor: "primary.main", bgcolor: (t) => alpha(t.palette.primary.main, 0.1) }
          : { borderColor: "divider", bgcolor: "surfaceSoft" },
      ]}
    >
      <BulkCheckbox
        checked={selected}
        onChange={onToggle}
        ariaLabel={`Select status ${status.name}`}
      />
      <Box sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        <StatusBadge status={status} />
      </Box>
      <TextField
        value={name}
        onChange={(e) => setName(e.target.value)}
        sx={{ minWidth: 0 }}
      />
      <Box
        component="input"
        type="color"
        value={color}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setColor(e.target.value)}
        title="Status colour"
        sx={{ height: 32, width: 48, cursor: "pointer", borderRadius: 0.5, border: 1, borderColor: "divider", bgcolor: "background.default" }}
      />
      <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 12, color: "text.disabled" }}>
        {status.category}
      </Box>
      <Button
        variant="contained"
        size="small"
        disabled={!dirty || isPending}
        onClick={() => onSave({ name, color })}
        sx={{ minWidth: 0, px: 1.5, py: 0.5, fontSize: 12 }}
      >
        Save
      </Button>
      <IconButton
        size="small"
        onClick={onDelete}
        title="Delete status"
        aria-label={`Delete status ${status.name}`}
        sx={{
          color: "text.disabled",
          "&:hover": { bgcolor: (t) => alpha(t.palette.error.main, 0.12), color: "error.main" },
        }}
      >
        <DeleteOutlineIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Box>
  );
}
